<script lang="ts" setup>
import { SancareOcticon } from '@sancare/typescript-commons'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { StaySuggestionSearch, SuggestionJustification } from '@/misc/types'
import JustificationSummary from '@/stay-displayer/diagnosis-lists/ApiJustificationSummary.vue'
import {
  DependenciesLabels,
  DependenciesSections,
  DependenciesSectionsLabels,
  DependenciesType
} from '@/store/modules/stay/smr/types'
import { StayHealthData } from '@/store/modules/stay/types'

import { HealthDataDocument } from '../../../../../../commons/typescript-commons/src/types/health-data'

const store = useStore()
const { t } = useI18n()

const props = defineProps<{
  dependencyType: DependenciesType
  dependencyValue: {
    lowValue: string
    highValue: string
    formattedValue: string
  } | null
  searches?: StaySuggestionSearch[]
  healthData?: StayHealthData | null
  suggestionJustifications?: Record<string, SuggestionJustification> | null
}>()

const searchNames = computed(() => props.searches?.map((search) => search.name) || [])
const searchIds = computed(() => props.searches.map((search) => search.sancareId))
const showJustificationSummary = ref(false)
const isSectionHeader = computed(() => Object.values(DependenciesSections).includes(props.dependencyType))
// The suggested value shown to the user is a merge across every matching saved search (see
// getSuggestedDependencies()), but it always ends up equal to one specific search's raw range —
// this reconstructs that raw "low_high" (or single "value") form, which is how the backend stores
// and looks suggestions up (suggested_label.dependency_value), from the low/high already computed.
const dependencyReference = computed(() => {
  if (!props.dependencyValue) {
    return null
  }
  const { lowValue, highValue } = props.dependencyValue
  return lowValue === highValue ? lowValue : `${lowValue}_${highValue}`
})
const apiJustification = computed(() => {
  if (!dependencyReference.value) {
    return null
  }
  return props.suggestionJustifications?.[`${props.dependencyType}:${dependencyReference.value}`] ?? null
})
const canShowRational = computed(() => Boolean(apiJustification.value))

function makeSelection() {
  store.commit('smrStay/setJustificationSelection', {
    labelType: props.dependencyType,
    labelReference: props.dependencyValue.formattedValue,
    savedSearchIds: searchIds.value,
  })
}

function toggleRationals() {
  showJustificationSummary.value = !showJustificationSummary.value
  if (showJustificationSummary.value) {
    makeSelection()
  }
}

function handleHealthDataSelection(mainContent: HealthDataDocument, scrollTarget: { docId: number, offset: number } | null = null) {
  store.commit('smrStay/setScrollTarget', scrollTarget)
  store.commit('smrStay/setMainContent', mainContent)
  makeSelection()
  toggleRationals()
}

function flagJustification(payload) {
  store.dispatch('smrStay/createFalseJustificationForSuggestion', payload)
}

function unflagJustification({ justificationId, docType, docId }) {
  store.dispatch('smrStay/deleteFalseJustification', {
    justificationId,
    reportId: docType === 'report' ? docId : null,
    healthEntryId: docType === 'textualHealthEntry' ? docId : null,
  })
}

const dependencyLabel = computed(() => {
  if (isSectionHeader.value) {
    return DependenciesSectionsLabels[props.dependencyType]
  }

  for (const sectionKey of Object.keys(DependenciesLabels)) {
    const section = DependenciesLabels[sectionKey]
    if (props.dependencyType in section) {
      return section[props.dependencyType]
    }
  }
  return props.dependencyType
})
</script>

<template>
  <div
    :class="{
      'dependency-section-title aligned-row mt-3': isSectionHeader,
      'dependency-line hoverable-item': !isSectionHeader,
      'no-suggestion': !props.dependencyValue
    }"
  >
    <div
      v-if="props.dependencyValue"
      class="d-flex justify-content-between"
    >
      <div
        class="d-flex align-items-center gap-1"
        :class="{
          'text-uppercase': isSectionHeader
        }"
      >
        {{ dependencyLabel }}: <strong>{{ dependencyValue.formattedValue }}</strong>
      </div>
      <div class="d-flex justify-content-between align-items-center">
        <v-dropdown
          :shown="showJustificationSummary"
          placement="left"
          class="popover-container flex-row align-items-center"
          popover-class="justification-popover"
          @auto-hide="showJustificationSummary = false"
        >
          <div
            v-if="searches && searches.length"
            v-tooltip.left="{
              content: searchNames.join(','),
            }"
            class="d-flex align-items-center gap-1"
            @click="toggleRationals"
          >
            <sancare-octicon
              name="search"
              :width="13"
              :height="13"
            />
            <small class="dependency-searches">
              {{ searchNames.join(', ') }}
            </small>
          </div>
          <template #popper>
            <justification-summary
              :is-loading="false"
              :justification="canShowRational ? apiJustification : null"
              :health-data="healthData"
              :label-type="dependencyType"
              :label-reference="dependencyReference"
              :no-value-label="canShowRational ? t('justification.noValueDueToSettings') : t('justification.noValue')"
              @select-health-data="handleHealthDataSelection"
              @flag-justification="flagJustification"
              @unflag-justification="unflagJustification"
            />
          </template>
        </v-dropdown>
      </div>
    </div>
    <div
      v-else
      class="d-flex align-items-center gap-1"
      :class="{
        'text-uppercase': isSectionHeader
      }"
    >
      {{ dependencyLabel }}: <strong>-</strong>
    </div>
  </div>
</template>


<style lang="scss" scoped>
.dependency-line {
  &:hover {
    background-color: rgba(0, 0, 0, .03);
  }
}

.dependency-searches {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60px;
}
</style>
